import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  HStack,
  Icon,
  Stack,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Textarea,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { useQuery, useMutation } from '@apollo/client';
import { FiAlertTriangle, FiCalendar, FiClock, FiDollarSign, FiExternalLink, FiInfo, FiMail, FiMessageSquare, FiPhone, FiUser, FiVideo, FiX } from 'react-icons/fi';
import { format } from 'date-fns';
import { usePageTitle } from '../../hooks/useDocumentTitle';
import { PoweredBy } from "./components/PoweredBy";
import { Tooltip } from "./components/Tip";
import { CalEmpty, CalError, CalLoading, CalPageHeader, CalSectionHeading, CalStatusPill, CalSurface, alpha, useCalTheme } from "./components/ui";
import { BookingShell, DetailRow } from "./booking/BookingParts";
import { GET_BOOKING_BY_TOKEN, CANCEL_BOOKING } from './queries';

export const ManageBooking = () => {
  usePageTitle('Manage Your Booking');
  const { token } = useParams<{ token: string }>();
  const toast = useToast();
  const { isOpen: isCancelModalOpen, onOpen: onCancelModalOpen, onClose: onCancelModalClose } = useDisclosure();
  const [cancelReason, setCancelReason] = useState('');
  const t = useCalTheme();

  // Query to get booking details
  const { data, loading, error, refetch } = useQuery(GET_BOOKING_BY_TOKEN, {
    variables: { token: token || '' },
    skip: !token,
    fetchPolicy: 'network-only', // Always fetch fresh data from server
  });

  // Mutation to cancel booking
  const [cancelBookingMutation, { loading: cancelling }] = useMutation(CANCEL_BOOKING, {
    onCompleted: (data) => {
      if (data.cancelBooking.success) {
        toast({
          title: 'Booking Cancelled',
          description: 'Your booking has been cancelled successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onCancelModalClose();
        // Refetch to update the booking status
        refetch();
      } else {
        toast({
          title: 'Cancellation Failed',
          description: data.cancelBooking.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) {
      toast({
        title: 'Reason Required',
        description: 'Please provide a reason for cancellation',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    await cancelBookingMutation({
      variables: {
        token: token || '',
        reason: cancelReason,
      },
    });
  };

  if (loading) {
    return (
      <BookingShell maxW="container.md" sunshine={false}>
        <CalLoading label="Loading your booking…" cards={2} columns={{ base: 1 }} />
        <PoweredBy />
      </BookingShell>
    );
  }

  // A failed request is not "not found": say which, and offer to try again.
  if (error) {
    return (
      <BookingShell maxW="container.md" sunshine={false}>
        <CalError title="We couldn't load your booking" error={error} onRetry={() => refetch()} />
        <PoweredBy />
      </BookingShell>
    );
  }

  if (!data?.getBookingByToken) {
    return (
      <BookingShell maxW="container.md" sunshine={false}>
        <CalEmpty icon={FiCalendar} title="Booking not found">
          This booking link is invalid or has expired. Please check your confirmation email for the correct link.
        </CalEmpty>
        <PoweredBy />
      </BookingShell>
    );
  }

  const booking = data.getBookingByToken;
  const metadata = booking.metadata || {};

  // Extract booking information from metadata
  const bookerName = metadata['X-BOOKER-NAME'] || 'Unknown';
  const bookerEmail = metadata['X-BOOKER-EMAIL'] || '';
  const bookerPhone = metadata['X-BOOKER-PHONE'] || '';
  const bookerTimezone = metadata['X-BOOKER-TIMEZONE'] || 'UTC';
  const bookingStatus = metadata['X-BOOKING-STATUS'] || 'CONFIRMED';
  const paymentStatus = metadata['X-PAYMENT-STATUS'] || 'NOT_REQUIRED';
  const paymentAmount = metadata['X-PAYMENT-AMOUNT'];
  const paymentCurrency = metadata['X-PAYMENT-CURRENCY'] || 'USD';
  const meetingLink = metadata['X-MEETING-LINK'];

  const startDate = new Date(booking.startTime);
  const endDate = new Date(booking.endTime);

  // Times are formatted in the viewer's own zone, so label them with that zone (not the zone it was booked from).
  let viewerZone = '';
  try { viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* older browsers */ }

  // Extract custom question answers
  const customAnswers: Array<{ question: string; answer: string }> = [];
  Object.keys(metadata).forEach(key => {
    if (key.startsWith('X-BOOKING-QUESTION-')) {
      const index = key.replace('X-BOOKING-QUESTION-', '');
      const answerKey = `X-BOOKING-ANSWER-${index}`;
      if (metadata[answerKey]) {
        customAnswers.push({
          question: metadata[key],
          answer: metadata[answerKey],
        });
      }
    }
  });

  const fieldBg = t.colorMode === 'dark' ? 'whiteAlpha.50' : 'white';

  return (
    <BookingShell maxW="container.md" sunshine={false}>
      <Stack spacing={{ base: 4, md: 6 }}>
        <CalPageHeader
          icon={FiCalendar}
          eyebrow="Your booking"
          title={booking.title || 'Manage your booking'}
          subtitle="Check the details, join the meeting, or cancel if you can't make it."
          badges={(
            <>
              <CalStatusPill kind="status" status={bookingStatus} />
              {paymentStatus !== 'NOT_REQUIRED' && <CalStatusPill kind="payment" status={paymentStatus} />}
            </>
          )}
        />

        {bookingStatus === 'CANCELLED' && (
          <HStack spacing={3} p={4} borderRadius="xl" bg={alpha(t.danger, 0.08)} borderWidth="1px" borderColor={alpha(t.danger, 0.3)} align="start">
            <Icon as={FiX} color={t.danger} mt={1} flexShrink={0} />
            <Text fontSize="md" color={t.text}>This booking has been cancelled. Nothing more is needed from you.</Text>
          </HStack>
        )}

        <CalSurface>
          <CalSectionHeading icon={FiCalendar} title="When" />
          <Stack spacing={4}>
            <DetailRow icon={FiCalendar} label="Date">{format(startDate, 'EEEE d MMMM yyyy')}</DetailRow>
            <DetailRow icon={FiClock} label={viewerZone ? `Time (your time zone, ${viewerZone})` : 'Time'}>
              {format(startDate, 'h:mm a')} – {format(endDate, 'h:mm a')}
              {viewerZone && bookerTimezone && bookerTimezone !== viewerZone && (
                <Text as="span" display="block" fontSize="sm" fontWeight="normal" color={t.faint}>Booked from {bookerTimezone}</Text>
              )}
            </DetailRow>
            {meetingLink && bookingStatus === 'CONFIRMED' && (
              <DetailRow icon={FiVideo} label="Meeting link">
                <Tooltip label="Open the online meeting in a new tab.">
                  <Button as="a" href={meetingLink} target="_blank" rel="noopener noreferrer" mt={1} size="lg" minH="48px"
                    bgImage={t.accentGradient} color="white" _hover={{ opacity: 0.92 }} rightIcon={<Icon as={FiExternalLink} />}>
                    Join meeting
                  </Button>
                </Tooltip>
              </DetailRow>
            )}
          </Stack>
        </CalSurface>

        <CalSurface>
          <CalSectionHeading icon={FiUser} title="Your details" why="What you gave when you booked." />
          <Stack spacing={4}>
            <DetailRow icon={FiUser} label="Name">{bookerName}</DetailRow>
            {bookerEmail && <DetailRow icon={FiMail} label="Email"><Text as="span" wordBreak="break-all">{bookerEmail}</Text></DetailRow>}
            {bookerPhone && <DetailRow icon={FiPhone} label="Phone">{bookerPhone}</DetailRow>}
            {paymentStatus !== 'NOT_REQUIRED' && (
              <DetailRow icon={FiDollarSign} label="Amount">
                {paymentAmount ? `${paymentCurrency} $${(paymentAmount / 100).toFixed(2)}` : 'Not recorded'}
              </DetailRow>
            )}
          </Stack>
        </CalSurface>

        {customAnswers.length > 0 && (
          <CalSurface>
            <CalSectionHeading icon={FiMessageSquare} title="Your answers" />
            <Stack spacing={4}>
              {customAnswers.map((qa, index) => (
                <Box key={index}>
                  <Text fontSize="sm" color={t.faint}>{qa.question}</Text>
                  <Text fontSize="md" fontWeight="semibold" color={t.text} wordBreak="break-word">{qa.answer}</Text>
                </Box>
              ))}
            </Stack>
          </CalSurface>
        )}

        {bookingStatus === 'CONFIRMED' && (
          <CalSurface>
            <CalSectionHeading icon={FiAlertTriangle} tone="warning" title="Can't make it?" />
            <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
              <Box flex={1}>
                <Tooltip label="Moving a booking online isn't built yet — pressing this only tells you so. Nothing changes.">
                  <Button
                    w="full"
                    variant="outline"
                    size="lg"
                    minH="48px"
                    color={t.muted}
                    borderColor={t.borderStrong}
                    borderStyle="dashed"
                    leftIcon={<Icon as={FiInfo} />}
                    onClick={() => toast({
                      title: 'Coming Soon',
                      description: 'Reschedule functionality will be available soon',
                      status: 'info',
                      duration: 3000,
                    })}
                  >
                    Reschedule (coming soon)
                  </Button>
                </Tooltip>
              </Box>
              <Box flex={1}>
                <Tooltip label="Opens a short form asking why. The booking is only cancelled when you confirm there.">
                  <Button w="full" colorScheme="red" variant="outline" size="lg" minH="48px" leftIcon={<Icon as={FiX} />} onClick={onCancelModalOpen}>
                    Cancel booking
                  </Button>
                </Tooltip>
              </Box>
            </Stack>
            <Text fontSize="sm" color={t.faint} mt={3}>
              Changing the time online isn't available yet. To move it, cancel this booking and book a new time.
            </Text>
          </CalSurface>
        )}

        <PoweredBy />
      </Stack>

      {/* Cancel Confirmation Modal */}
      <Modal isOpen={isCancelModalOpen} onClose={onCancelModalClose} size={{ base: "full", md: "md" }}>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg={t.colorMode === 'dark' ? 'gray.900' : 'white'} borderColor={t.border} borderWidth="1px" borderRadius={{ base: 0, md: '2xl' }}>
          <ModalHeader color={t.text}>Cancel this booking?</ModalHeader>
          <ModalBody>
            <Stack spacing={4}>
              <Text fontSize="md" color={t.muted}>
                The person you booked with will be told. This can't be undone — you would need to book again.
              </Text>
              <FormControl isRequired>
                <FormLabel fontSize="md" color={t.text}>Why are you cancelling?</FormLabel>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="A short reason is enough"
                  bg={fieldBg}
                  borderColor={t.borderStrong}
                  color={t.text}
                  fontSize="md"
                  rows={3}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Stack direction={{ base: 'column-reverse', sm: 'row' }} spacing={3} w="full" justify="flex-end">
              <Tooltip label="Close this and keep your booking. Nothing changes.">
                <Button variant="ghost" size="lg" minH="48px" onClick={onCancelModalClose}>
                  Keep booking
                </Button>
              </Tooltip>
              <Tooltip label="Cancel the booking now. The person you booked with is told.">
                <Button colorScheme="red" size="lg" minH="48px" onClick={handleCancelBooking} isLoading={cancelling} loadingText="Cancelling…">
                  Cancel booking
                </Button>
              </Tooltip>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </BookingShell>
  );
};

export default ManageBooking;
